import { type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";

/**
 * The sidebar stays fixed; only the content column "turns" when you move
 * between sections. Re-keying on the leading path segment (Work / Decisions /
 * Agents / Teams / Settings) gives one entrance per section change while
 * leaving sub-routes and filter switches (a view change on Work, a ticket
 * opening) untouched: those surfaces own their internal transitions and should
 * not re-settle the page.
 */
function sectionKey(pathname: string): string {
  return pathname.split("/").filter(Boolean)[0] ?? "work";
}

// Ease-out-expo, the curve already used across the settle animations in
// index.css. Quick to start, long gentle settle.
const SETTLE_EASE = [0.16, 1, 0.3, 1] as const;

/**
 * The page settle for section navigation. Enter-only by design: the outgoing
 * section unmounts instantly (so the live Outlet context keeps flowing), and
 * the incoming section fades up into place. Honours prefers-reduced-motion by
 * rendering children straight through.
 *
 * Opacity-dominant cross-fade with a 3px lift. The y stays tiny because the
 * Triptych surfaces render a 100vh stage inside an overflow-hidden main, so a
 * larger translate would clip the pinned composer during the settle. The
 * wrapper is a plain block whose child (the .tri-stage, height:100vh) sets its
 * own height.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const reduce = useReducedMotion();

  if (reduce) return <>{children}</>;

  return (
    <motion.div
      key={sectionKey(location.pathname)}
      initial={{ opacity: 0, y: 3 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: SETTLE_EASE }}
      style={{ willChange: "transform, opacity" }}
    >
      {children}
    </motion.div>
  );
}
